import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn, pluralize } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';

import { subscriptionMeOptions } from '@/entities/subscription';

const trialBannerVariants = cva('items-center justify-center gap-3 bg-primary/30', {
    variants: {
        variant: {
            default: 'hidden xs:flex px-3 py-2 rounded-lg',
            mobile: 'flex xs:hidden py-3 w-full',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

type Props = {
    className?: string;
} & VariantProps<typeof trialBannerVariants>;

export function TrialBanner({ className, variant }: Props) {
    const navigate = useNavigate();
    const { data: subscription } = useQuery(subscriptionMeOptions());
    const daysRemaining = subscription?.trialDaysRemaining ?? null;

    if (!daysRemaining) {
        return null;
    }

    const label =
        daysRemaining === 1
            ? 'Your trial ends today'
            : `Your trial expires in ${pluralize(daysRemaining, 'day')}`;

    const onUpgrade = () => navigate({ to: '/settings/plan-billing' });

    return (
        <div className={cn(trialBannerVariants({ variant, className }))}>
            <span className="text-sm leading-none">{label}</span>
            <Button size="sm" onClick={onUpgrade} className="text-sm leading-3">
                Upgrade
            </Button>
        </div>
    );
}
